import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  MapPin, Clock, Briefcase, DollarSign, ChevronDown, ChevronUp,
  CheckCircle2, Users, Zap, Heart, GraduationCap, Send
} from 'lucide-react';
import { CTABanner } from '../components/shared/CTABanner';
import { SectionHeader } from '../components/shared/SectionHeader';

const benefits = [
  {
    icon: Heart,
    title: 'Health & Wellness',
    description: 'Comprehensive medical, dental, and vision insurance for you and your family.'
  },
  {
    icon: DollarSign,
    title: 'Competitive Pay',
    description: 'Industry-leading salaries with performance bonuses and equity options.'
  },
  {
    icon: GraduationCap,
    title: 'Growth & Development',
    description: 'Professional development budget, certifications, and career advancement paths.'
  },
  {
    icon: Users,
    title: 'Great Team',
    description: 'Work alongside talented engineers and industry experts passionate about sustainability.'
  }
];

const jobListings = [
  {
    id: 'ee-001',
    title: 'Electrical Engineer',
    department: 'Engineering',
    location: 'Davie, FL',
    type: 'Full-time',
    salary: '$85,000 - $120,000',
    posted: 'Jan 2025',
    description: 'We are seeking an experienced Electrical Engineer to join our growing team. You will be responsible for designing and engineering EV charging infrastructure systems for commercial, fleet, and municipal clients across the nation.',
    responsibilities: [
      'Design electrical systems for EV charging installations including load calculations, single-line diagrams, and construction drawings',
      'Perform site assessments and electrical load analysis for new charging deployments',
      'Coordinate with utilities on service upgrades, interconnection requirements, and demand management',
      'Develop specifications for charging equipment, switchgear, transformers, and related electrical components',
      'Review and approve contractor submittals and RFIs during construction',
      'Ensure all designs comply with NEC, local codes, and utility standards',
      'Collaborate with project managers and field teams to support installations',
      'Conduct commissioning and troubleshooting of charging systems as needed'
    ],
    requirements: [
      'Bachelor\'s degree in Electrical Engineering or related field',
      'PE license preferred or ability to obtain within 12 months',
      '3+ years of experience in electrical design, preferably in power distribution or EV infrastructure',
      'Proficiency with AutoCAD, Revit, or similar design software',
      'Strong understanding of NEC Article 625 and utility interconnection standards',
      'Experience with load flow analysis and power system studies',
      'Excellent communication skills and ability to work with cross-functional teams',
      'Valid driver\'s license and ability to travel to project sites (up to 25%)'
    ],
    preferred: [
      'Experience with EV charging systems (Level 2 and DC Fast Charging)',
      'Knowledge of battery energy storage systems',
      'Familiarity with OCPP protocols and smart charging technology',
      'Experience with renewable energy integration'
    ]
  }
];

const JobCard = ({ job }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      data-testid={`job-card-${job.id}`}
    >
      {/* Job Header */}
      <div 
        className="p-6 md:p-8 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-gradient-to-r from-[#00A2B5]/10 to-[#36D890]/10 rounded-full text-xs font-semibold text-[#00A2B5]">
                {job.department}
              </span>
              <span className="text-xs text-slate-500">{job.posted}</span>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">{job.title}</h3>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {job.location}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {job.type}
              </span>
              <span className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                {job.salary}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              className="flex items-center gap-2 text-[#00A2B5] font-semibold"
              data-testid={`toggle-job-${job.id}`}
            >
              {isExpanded ? 'Hide Details' : 'View Details'}
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-slate-200"
        >
          <div className="p-6 md:p-8 space-y-8">
            {/* Description */}
            <div>
              <p className="text-slate-600 leading-relaxed">{job.description}</p>
            </div>

            {/* Responsibilities */}
            <div>
              <h4 className="font-bold text-slate-900 mb-4">Responsibilities</h4>
              <ul className="space-y-3">
                {job.responsibilities.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00C5A5] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Requirements */}
            <div>
              <h4 className="font-bold text-slate-900 mb-4">Requirements</h4>
              <ul className="space-y-3">
                {job.requirements.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00A2B5] mt-2 flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Preferred */}
            <div>
              <h4 className="font-bold text-slate-900 mb-4">Preferred Qualifications</h4>
              <ul className="space-y-3">
                {job.preferred.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Apply Button */}
            <div className="pt-4 border-t border-slate-200">
              <a
                href={`mailto:careers@SkytecEV.com?subject=Application: ${job.title}&body=I am interested in applying for the ${job.title} position.%0D%0A%0D%0APlease attach your resume and cover letter.`}
                className="btn-gradient inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold group"
                data-testid={`apply-${job.id}`}
              >
                <Send className="w-5 h-5" />
                Apply for This Position
              </a>
              <p className="text-sm text-slate-500 mt-3">
                Send your resume and cover letter to careers@SkytecEV.com
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export const CareersPage = () => {
  return (
    <main className="pt-20" data-testid="careers-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50 circuit-pattern" data-testid="careers-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2B5] mb-4 block">
              Careers
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6">
              Join the Team{' '}
              <span className="gradient-text">Leading the Charge</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Help us build the infrastructure that powers the future of electric mobility. We're looking for talented people passionate about sustainability and innovation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-join">
        <div className="container-custom">
          <SectionHeader
            overline="Why Skytec EV"
            title="Build Your Career With Us"
            centered
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00A2B5]/10 to-[#36D890]/10 flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-[#00A2B5]" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="open-positions">
        <div className="container-custom">
          <SectionHeader
            overline="Open Positions"
            title="Current Opportunities"
            subtitle="Join our growing team and help shape the future of EV infrastructure."
          />

          <div className="space-y-6">
            {jobListings.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>

          {jobListings.length === 0 && (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No open positions at this time. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* General Application */}
      <section className="py-20 md:py-28 bg-white" data-testid="general-application">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-4">Don't See the Right Fit?</h2>
            <p className="text-lg text-slate-600 mb-8">
              We're always looking for talented individuals. Send us your resume and tell us how you can contribute to our mission.
            </p>
            <a
              href="mailto:careers@SkytecEV.com?subject=General Application&body=I am interested in joining the Skytec EV team.%0D%0A%0D%0APlease attach your resume."
              className="btn-gradient inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold group"
              data-testid="general-apply"
            >
              <Send className="w-5 h-5" />
              Send Your Resume
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner 
        title="Ready to Make an Impact?"
        subtitle="Join us in building the infrastructure that powers the future of transportation."
        primaryCTA="View Open Positions"
        primaryLink="#open-positions"
        variant="dark"
      />
    </main>
  );
};

export default CareersPage;
